'use client';

/**
 * usePinnedCard — voice-call pinned-card HISTORY (design turn 14, building on
 * turn 11 part B). Was a single-slot `useState<PinnedCard | null>` where a
 * new pin plainly replaced the old one; reworked into a capped history
 * (newest first, `MAX_PINS`) so an earlier pinned video isn't simply gone the
 * moment a second tool result arrives — "never a second inbox" (owner
 * decision): the stack peeks the last few pins rather than growing without
 * bound.
 *
 * `pinnedCard` (the front entry's card, or null) stays exposed for
 * backward-compat with existing consumers that only ever read the front pin
 * (desktop's presentation, ChatContext's own toggleCall wrapper) — the whole
 * history is additionally exposed as `pinHistory` for the mobile stack UI
 * (14c). All push/cap/bring-to-front/dismiss-front policy is the pure
 * pin-history.ts module (see its doc comment) — this hook is just a thin
 * useState wrapper around it, same split as useHandoff.ts.
 */

import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { PinnedCard } from '@/lib/pin-routing';
import {
  pushPin,
  dismissFrontPin,
  bringToFront,
  type PinEntry,
} from '@/lib/pin-history';

export function usePinnedCard() {
  const [pinHistory, setPinHistory] = useState<PinEntry[]>([]);

  // Unconditional push — even a re-pin of content similar to what's already
  // showing is still a new pin (see pushPin's doc comment: no de-dup/merge
  // heuristic). id/pinnedAt are minted here (not in the pure module) so
  // pushPin itself stays a plain, deterministic array operation to test.
  const pinCard = useCallback((card: PinnedCard) => {
    setPinHistory((prev) =>
      pushPin(prev, { id: uuidv4(), pinnedAt: Date.now(), card })
    );
  }, []);

  // The dismiss (x) affordance — pops ONLY the front pin; the next most
  // recent (if any) becomes front. See dismissFrontPin's doc comment.
  const dismissPin = useCallback(() => {
    setPinHistory((prev) => dismissFrontPin(prev));
  }, []);

  // 14c: tapping an older row in the expanded stack promotes it to front.
  const bringPinToFront = useCallback((id: string) => {
    setPinHistory((prev) => bringToFront(prev, id));
  }, []);

  // Call-lifecycle reset ("this pin belongs to the call it was pinned
  // during") — clears the WHOLE history, unlike dismissPin's front-only pop.
  // See ChatContext.tsx's toggleCall wrapper for the call sites.
  const clearPins = useCallback(() => {
    setPinHistory([]);
  }, []);

  return {
    pinnedCard: pinHistory[0]?.card ?? null,
    pinHistory,
    pinCard,
    dismissPin,
    bringPinToFront,
    clearPins,
  };
}
